import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Check, X } from "lucide-react";
import api, { apiError } from "@/lib/api";

// Held prize awards (risk / value / critical violation). A hold is NOT a disqualification.
export default function PrizeHolds() {
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});
  const load = () => {
    api.get("/admin/prize-awards?status=held").then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/integrity-settings").then((r) => setCfg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const act = async (id, kind, reassign = false) => {
    setBusy(id + kind);
    try {
      const { data } = await api.post(`/admin/prize-awards/${encodeURIComponent(id)}/${kind}`, { note: notes[id] || "", reassign });
      toast.success(kind === "approve" ? "Approved & paid" : data.reassigned_to ? `Rejected - reassigned (${data.status})` : (data.detail || "Rejected"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const saveCfg = async () => {
    try { await api.post("/admin/integrity-settings", { risk_threshold: Number(cfg.risk_threshold), hold_value_usd: Number(cfg.hold_value_usd) }); toast.success("Hold thresholds saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-race-surface border border-race-line mb-4" data-testid="prize-holds">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-race-line">
        <div className="flex items-center gap-2"><ShieldAlert size={16} className="text-amber-500" /><span className="font-semibold text-sm">Prize Holds</span>
          <span className="font-mono text-[11px] text-race-sub">({rows.length})</span></div>
        {cfg && (
          <div className="flex items-center gap-2 text-xs font-mono text-race-sub">
            Hold if risk ≥ <input type="number" value={cfg.risk_threshold} onChange={(e) => setCfg({ ...cfg, risk_threshold: e.target.value })} className="w-14 bg-race-bg border border-race-line px-1 py-0.5" data-testid="hold-risk-threshold" />
            or prize ≥ $<input type="number" value={cfg.hold_value_usd} onChange={(e) => setCfg({ ...cfg, hold_value_usd: e.target.value })} className="w-16 bg-race-bg border border-race-line px-1 py-0.5" data-testid="hold-value-usd" />
            <button onClick={saveCfg} className="px-2 py-0.5 border border-race-line hover:bg-race-elevated" data-testid="hold-settings-save">Save</button>
          </div>
        )}
      </div>
      {rows.length === 0 ? <div className="px-4 py-5 text-sm text-race-sub" data-testid="prize-holds-empty">No held prizes. Suspicious or high-value wins land here before any payout.</div> : (
        <div className="divide-y divide-race-line">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3 space-y-1.5" data-testid={`prize-hold-${r.id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-semibold">{r.racer || r.email}</span>
                <span className="text-race-sub">{r.contest_name}</span>
                <span className="font-mono text-xs">{r.final_time}s · risk <b className={r.risk_score >= 50 ? "text-red-500" : ""}>{r.risk_score}</b></span>
                <span className="font-mono text-xs text-race-sub">{r.prize_type === "cash" ? `${r.amount_credits} cr` : `$${r.prize_fmv_usd} ${r.prize_type}`}</span>
              </div>
              <div className="text-xs text-amber-500">{(r.hold_reasons || []).join(" · ")}</div>
              {r.risk_signals && Object.keys(r.risk_signals).length > 0 && (
                <div className="text-[11px] font-mono text-race-sub">Signals: {Object.entries(r.risk_signals).map(([k, v]) => `${k} (+${v})`).join(", ")}</div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <input placeholder="Review note (saved to audit log)" value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                  className="flex-1 min-w-[180px] bg-race-bg border border-race-line px-2 py-1 text-xs" data-testid={`prize-hold-note-${r.id}`} />
                <button disabled={!!busy} onClick={() => act(r.id, "approve")} data-testid={`prize-hold-approve-${r.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-green-500/60 text-green-500 hover:bg-green-500/10 disabled:opacity-50"><Check size={12} /> Approve & release</button>
                <button disabled={!!busy} onClick={() => act(r.id, "reject")} data-testid={`prize-hold-reject-${r.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-red-500/60 text-red-500 hover:bg-red-500/10 disabled:opacity-50"><X size={12} /> Reject</button>
                <button disabled={!!busy} onClick={() => act(r.id, "reject", true)} data-testid={`prize-hold-reassign-${r.id}`}
                  className="px-2.5 py-1 text-xs border border-race-line hover:bg-race-elevated disabled:opacity-50">Reject & award next eligible</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
